import { Illustration, Text, Title } from "@clipboard-health/ui-components";
import { Box, Checkbox, DialogContent, FormControlLabel, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Controller, useForm } from "react-hook-form";

interface PolicyAcknowledgementStepProps {
  workplaceName: string;
  breakPolicy: string;
  mandatoryBreakDurationInMinutes: number;
  onContinue: () => void;
  onBack: () => void;
}

interface FormData {
  acknowledged: boolean;
}

export function PolicyAcknowledgementStep({
  workplaceName,
  breakPolicy,
  mandatoryBreakDurationInMinutes,
  onContinue,
  onBack,
}: PolicyAcknowledgementStepProps) {
  const { control, watch } = useForm<FormData>({
    defaultValues: {
      acknowledged: false,
    },
  });

  const acknowledged = watch("acknowledged");

  return (
    <>
      <DialogContent sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}>
        <Stack spacing={4} direction="column" alignItems="center" my={4}>
          <Box pt={4}>
            <Illustration type="break" />
          </Box>
          <Title component="h1" sx={{ textAlign: "center" }}>
            Why didn&apos;t you take a break?
          </Title>
        </Stack>

        <Stack spacing={5} mt={5}>
          <Text>
            You are attempting to submit a timesheet with an incomplete break. {workplaceName}{" "}
            requires that you take a {mandatoryBreakDurationInMinutes}-minute unpaid break.
          </Text>
          <Text>
            As a reminder, {workplaceName} has the following mandatory break policy which you
            acknowledged earlier:
          </Text>
          <Text variant="body1" sx={{ fontStyle: "italic" }}>
            {breakPolicy}
          </Text>
          <Text>
            If you believe your incomplete break is accurate and is compliant with the
            workplace&apos;s break policy, please continue
          </Text>
        </Stack>
      </DialogContent>
      <DialogFooter
        withShadow
        orientation="vertical"
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary }}
      >
        <Stack direction="column">
          <Controller
            name="acknowledged"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={field.onChange} />}
                label={
                  <Text variant="body2">
                    I acknowledge {workplaceName}&apos;s break policy, and my incomplete break is
                    consistent with this policy
                  </Text>
                }
                sx={{ alignSelf: "flex-start", mb: 5, px: 4 }}
              />
            )}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ my: 3 }}>
            <IconButton
              variant="outlined"
              iconType="arrow-left"
              size="large"
              aria-label="Go back"
              onClick={onBack}
            />
            <Button
              sx={{ minWidth: 180 }}
              variant="contained"
              size="large"
              aria-label="Continue"
              disabled={!acknowledged}
              onClick={() => {
                onContinue();
              }}
            >
              Continue
            </Button>
          </Stack>
        </Stack>
      </DialogFooter>
    </>
  );
}
